import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Callout, EmptyStateNoResults, ExternalLink, LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn, List, ListItemContent, ListItemLink, ListTitle, Pagination, SearchInput, } from "@octopusdeploy/design-system-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository, ResourceCollection, TagSetResource, TagSetScope } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { sortBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import type * as React from "react";
import useIsMultiTenancyEnabledFeatureFlag from "~/areas/configuration/hooks/useIsMultiTenancyEnabledFeatureFlag";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import MarkdownDescription from "~/components/MarkdownDescription";
import { SupportedTagSetScopes, TagSetScopeMultiSelect } from "~/components/MultiSelect/TagSetScopeMultiSelect";
import { SupportedTagSetTypes, TagSetTypeMultiSelect } from "~/components/MultiSelect/TagSetTypeMultiSelect";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import Tag from "~/components/Tag/index";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import TagSetsSorter from "./TagSetsSorter";
import styles from "./style.module.less";
export interface TagSetsQueryParams {
    tagSetName: string;
    scopes?: string[];
    types?: string[];
    page: number | undefined;
    pageSize: number | undefined;
}
export interface TagSetsProps {
    spaceId: string;
    queryParams: TagSetsQueryParams;
    setQueryParams: QueryParamValuesSetter<TagSetsQueryParams>;
    loaderData: TagSetsLoaderData;
}
interface Filter extends TagSetsQueryParams {
}
export interface TagSetsLoaderData {
    tagSets: ResourceCollection<TagSetResource>;
    hasTagSets: boolean;
}
const getPageTitle = (isExtendedTagSetsEnabled: boolean) => (isExtendedTagSetsEnabled ? "Tag Sets" : "Tenant Tag Sets");
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const localStorageKeyForDismissedCallout = "Octopus.Callout.TagSets.Dismissed";
const defaultTagSetSummaryFilter: Filter = {
    tagSetName: "",
    scopes: [],
    types: [],
    page: 1,
    pageSize: defaultPageSize,
};
export async function tagSetsPageLoader(repository: Repository, queryParams: TagSetsQueryParams): Promise<TagSetsLoaderData> {
    return await getPaginatedTagSets(repository, queryParams);
}
export function TagSets({ spaceId, queryParams, setQueryParams, loaderData }: TagSetsProps) {
    const navigation = useSpaceAwareNavigation();
    const isMultiTenancyEnabled = useIsMultiTenancyEnabledFeatureFlag();
    const isExtendedTagSetsEnabled = isFeatureToggleEnabled("extended-tag-sets");
    const isTargetTagSetsEnabled = isFeatureToggleEnabled("TargetTagSetsFeatureToggle");
    const sortDialogControls = useDialogTrigger();
    const [debouncedVariableSetName, setDebouncedVariableSetName] = useDebouncedExternalState(queryParams.tagSetName, (value) => setQueryParams({ tagSetName: value, page: 1 }), 200);
    const { result: pagedTagSetsQueryResult, refetch: reloadTagSets } = usePagedTagSetsQuery(queryParams, loaderData);
    const { tagSets, hasTagSets } = pagedTagSetsQueryResult;
    const isFiltered = isFiltering(queryParams);
    useEffect(() => {
        if (!isExtendedTagSetsEnabled && !isMultiTenancyEnabled) {
            navigation.redirect(links.tenantsPage.generateUrl({ spaceId }));
        }
    }, [isExtendedTagSetsEnabled, isMultiTenancyEnabled, navigation, spaceId]);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const [calloutDismissed, setCalloutDismissed] = useState<boolean>(localStorage.getItem(localStorageKeyForDismissedCallout) === "true");
    const addTagSetPageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.TagSetCreate }),
        label: isExtendedTagSetsEnabled ? "Add Tag Sets" : "Create Tag Set",
        path: links.createTagSetPage.generateUrl({ spaceId }),
    };
    if (!hasTagSets) {
        return (<PortalEmptyStateOnboarding title={isExtendedTagSetsEnabled ? "Create Tag Sets to easily classify and group tenants" : "Create tenant tags to easily classify and group tenants"} primaryAction={addTagSetPageAction} description={isExtendedTagSetsEnabled
                ? "Tag Sets help you to classify your tenants using fields that meet your needs, and tailor tenanted deployments for your projects and environments."
                : "Tenant tags help you to classify your tenants using custom tags that meet your needs, and tailor tenanted deployments for your projects and environments."} learnMore={<ExternalLink href="MultiTenantTags">Learn more</ExternalLink>}/>);
    }
    const availableScopes = isTargetTagSetsEnabled ? SupportedTagSetScopes : SupportedTagSetScopes.filter((scope) => scope !== "Target");
    const tagSetScopes = availableScopes.map((scope) => ({ Id: scope, Name: scope }));
    const tagSetTypes = SupportedTagSetTypes.map((type) => ({ Id: type, Name: type }));
    const hideBottomPagination = tagSets.Items.length < 20;
    const advancedFilters = (<AdvancedFilters>
            <TagSetScopeMultiSelect value={queryParams.scopes ?? []} label="Tag Sets scopes" items={tagSetScopes} onChange={(scopes) => setQueryParams((prev) => ({ ...prev, scopes }))}/>
            <TagSetTypeMultiSelect value={queryParams.types ?? []} label="Tag Sets types" items={tagSetTypes} onChange={(types) => setQueryParams((prev) => ({ ...prev, types }))}/>
        </AdvancedFilters>);
    const filters = {
        inputs: [<SearchInput value={debouncedVariableSetName} onChange={setDebouncedVariableSetName} accessibleName={"Search by tag set name"} placeholder={"Filter by tag set name..."}/>],
        advancedFilters: isExtendedTagSetsEnabled && tagSetScopes.length > 1
            ? {
                content: advancedFilters,
                onResetFilter: () => {
                    setQueryParams(defaultTagSetSummaryFilter);
                },
                hasUserSelectedValues: isFiltered,
            }
            : undefined,
    };
    return (<PageContent pagination={{
            ui: (<Pagination label={"Tag Sets"} totalResults={tagSets.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={tagSets.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} header={{ title: getPageTitle(isExtendedTagSetsEnabled), primaryAction: addTagSetPageAction, pageActions: getPageActions(sortDialogControls.openDialog) }} filters={filters}>
            <>
                {calloutDismissed ? (<></>) : (<div style={{ marginBottom: 10 }}>
                        <Callout type={"information"} title="Tenant Tag Sets are expanding" canClose={true} onClose={() => onCloseCallout(setCalloutDismissed)}>
                            <div>
                                The Tenant Tag Sets feature is expanding. This update provides greater flexibility, allowing you to define and capture values tailored to your deployment needs. We'd love to hear{" "}
                                <ExternalLink href={"field-sets-survey"}>your feedback</ExternalLink>.
                            </div>
                        </Callout>
                    </div>)}

                <ContentBorder>
                    <Dialog open={sortDialogControls.isOpen}>
                        <TagSetsSorter onSaved={reloadTagSets}/>
                    </Dialog>
                    {isExtendedTagSetsEnabled ? (tagSets.Items.length > 0 ? (<TagSetsTable tagSets={tagSets.Items} spaceId={spaceId}/>) : (<EmptyStateNoResults />)) : (<List items={tagSets.Items} renderRow={({ item }) => <TagSetListItem spaceId={spaceId} tagSet={item}/>} rowKey={(item) => item.Id} empty={<EmptyStateNoResults />}/>)}
                </ContentBorder>
            </>
        </PageContent>);
}
function onCloseCallout(setCalloutDismissed: (setCalloutDismissed: ((prevState: boolean) => boolean) | boolean) => void) {
    localStorage.setItem(localStorageKeyForDismissedCallout, true.toString());
    setCalloutDismissed(true);
}
interface GetPaginatedTagSetsOptions {
    page: number | undefined;
    pageSize: number | undefined;
    tagSetName: string | undefined;
    scopes?: string[];
    types?: string[];
}
async function getPaginatedTagSets(repository: Repository, { page = 1, pageSize = defaultPageSize, tagSetName = "", scopes = [], types = [] }: GetPaginatedTagSetsOptions) {
    const tagSets = repository.TagSets.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: tagSetName,
        scopes: scopes.length > 0 ? scopes : undefined,
        types: types.length > 0 ? types : undefined,
    });
    const tagSetsStats = repository.TagSets.list({ skip: 0, take: 0 });
    return {
        tagSets: await tagSets,
        hasTagSets: (await tagSetsStats).TotalResults > 0,
    };
}
function usePagedTagSetsQuery(queryParams: TagSetsQueryParams, loaderData: TagSetsLoaderData) {
    return useQuery((repository) => getPaginatedTagSets(repository, { page: queryParams.page, pageSize: queryParams.pageSize, tagSetName: queryParams.tagSetName, scopes: queryParams.scopes, types: queryParams.types }), [queryParams.page, queryParams.pageSize, queryParams.tagSetName, queryParams.scopes, queryParams.types], "Get paginated tag sets", {
        initialResult: loaderData,
    });
}
function isFiltering(queryParams: TagSetsQueryParams): boolean {
    return queryParams.tagSetName !== "" || (queryParams.scopes?.length ?? 0) > 0 || (queryParams.types?.length ?? 0) > 0;
}
function getPageActions(openReorderDialogClick: () => void): PageAction[] {
    const sortDialogPageAction: PageAction = {
        type: "button",
        buttonType: "secondary",
        hasPermissions: isAllowed({ permission: Permission.TagSetCreate }),
        label: "Reorder",
        onClick: openReorderDialogClick,
    };
    return [sortDialogPageAction];
}
function TagSetListItem({ tagSet, spaceId }: {
    tagSet: TagSetResource;
    spaceId: string;
}) {
    return (<ListItemLink href={links.tagSetPage.generateUrl({ spaceId, tagSetId: tagSet.Id })}>
            <ListItemContent>
                <ListTitle>{tagSet.Name}</ListTitle>
                <div className={styles.helpText}>
                    <MarkdownDescription markup={tagSet.Description}/>
                </div>
                <div>
                    {sortBy(tagSet.Tags, (t) => t.SortOrder).map((t) => (<Tag tagName={t.Name} tagColor={t.Color} key={t.Id} description={t.Description} borderColor={themeTokens.color.border.primary}/>))}
                </div>
            </ListItemContent>
        </ListItemLink>);
}
function TagSetsTable({ tagSets, spaceId }: {
    tagSets: TagSetResource[];
    spaceId: string;
}) {
    const formatScope = (scope?: TagSetScope[]) => {
        if (!scope || scope.length === 0) {
            return "Not specified";
        }
        return scope.join(", ");
    };
    return (<LegacyDataTable>
            <LegacyDataTableHeader>
                <LegacyDataTableRow>
                    <LegacyDataTableHeaderColumn>Name</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Scope</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Type</LegacyDataTableHeaderColumn>
                </LegacyDataTableRow>
            </LegacyDataTableHeader>
            <LegacyDataTableBody>
                {tagSets.map((tagSet) => (<LegacyDataTableRow key={tagSet.Id}>
                        <LegacyDataTableRowColumn>
                            <InternalLink to={links.tagSetPage.generateUrl({ spaceId, tagSetId: tagSet.Id })}>{tagSet.Name}</InternalLink>
                        </LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>{formatScope(tagSet.Scopes)}</LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>{getTagSetTypeDisplayValue(tagSet.Type)}</LegacyDataTableRowColumn>
                    </LegacyDataTableRow>))}
            </LegacyDataTableBody>
        </LegacyDataTable>);
}
function getTagSetTypeDisplayValue(type: string | undefined) {
    switch (type) {
        case "SingleSelect":
            return "Single Select";
        case "MultiSelect":
            return "Multi Select";
        case "FreeText":
            return "Free Text";
        default:
            return "Not specified";
    }
}
function ContentBorder({ children }: {
    children: React.ReactNode;
}) {
    return <div className={contentBorderStyles}>{children}</div>;
}
const contentBorderStyles = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.medium,
});
